import {
  object,
  number,
  string,
  array,
  any,
  Infer,
  is,
  nullable,
  boolean
} from 'superstruct';

import type { Competition } from '../competition';
import type { StateType } from './reducer';

const SERVER_ERROR_MESSAGE = 'The server responded with bad data.';

/** GET QUESTIONS RESPONSE */
const ImageJson = object({
  altText: nullable(string()),
  url: nullable(string()),
  largeUrl: nullable(string())
});

const AnswerJson = object({
  id: number(),
  answer: string(),
  image: nullable(ImageJson),
  notes: nullable(string())
});

const QuestionJson = object({
  id: number(),
  type: number(),
  question: string(),
  answers: array(AnswerJson),
  notes: nullable(string()),
  image: nullable(ImageJson),
  commentsRequired: boolean()
});

const QuestionsJson = array(QuestionJson);

const getQuestionsResponse = object({
  questions: QuestionsJson,
  attemptsRemaining: nullable(number())
});

const errorResponse = object({
  Message: string()
});

export type GetQuestionsResponse = Infer<typeof getQuestionsResponse>;
export type QuestionsWithPassable = GetQuestionsResponse & { passable: StateType['passable'] };
export type GetQuestionsExceededRetriesResponse = Infer<typeof errorResponse>;
export type QuestionJson = Infer<typeof QuestionJson>;
export type QuestionsJson = Infer<typeof QuestionsJson>;
export type AnswerJson = Infer<typeof AnswerJson>;

//** SUBMISSION RESPONSE */

const badgePlaceholders = array(
  object({
    name: string(),
    pageTiger: boolean(),
    tag: string(),
    value: nullable(string())
  })
);

const feedback = nullable(
  object({
    questions: array(
      object({
        answers: array(
          object({
            answer: string(),
            chosen: boolean(),
            correct: number(),
            notes: string(),
            score: number(),
            image: nullable(
              object({
                altText: nullable(string()),
                url: nullable(string()),
                largeUrl: nullable(string())
              })
            )
          })
        ),
        notes: string(),
        question: string(),
        type: number(),
        comment: nullable(string())
      })
    ),
    totalScore: number()
  })
);

const result = nullable(
  object({
    badgePlaceholderList: badgePlaceholders,
    chains: object({
      chains: array(any())
    }),
    moduleCompleted: boolean(),
    // Neutral = 0,Passed = 1,Failed = 2
    passed: number(),
    popup: object({
      backgroundColour: string(),
      borderColour: string(),
      borderSize: number(),
      disableClose: boolean(),
      height: number(),
      marginSize: number(),
      width: number()
    }),
    readerBadgeID: number(),
    resultHtml: string(),
    resultID: number()
  })
);

const submissionResponse = object({
  id: number(),
  feedback: feedback,
  result: result
});

export type SubmissionResponse = Infer<typeof submissionResponse>;
export type BadgePlaceholders = Infer<typeof badgePlaceholders>;
export type CompFeedback = Infer<typeof feedback>;

const processJsonResponse = async (res: Response): Promise<unknown> => {
  if (res.ok) {
    return Promise.resolve(res.json());
  }

  if (res.status === 401) {
    window.location.assign(window.location.href);
    return Promise.reject(new Error('You are no longer authenticated.'));
  }

  // A 400 response _should_ be JSON we a message property which we will use to reject
  if (res.status === 400) {
    const jsonResponse = await res.json();

    return Promise.reject(new Error(jsonResponse.Message));
  }

  return Promise.reject(new Error('There was a problem communicating with the server.'));
};

const permissionsCheckResponse = object({
  attemptsRemaining: nullable(number()),
  feedback: nullable(feedback),
  passable: boolean(),
  result: nullable(result),
  // status can be 1 Unknown, 2 OK, 3 Passed, 4 Failed
  status: number()
});

export type PermissionsCheckResponse = Infer<typeof permissionsCheckResponse>;

const permissionsCheck = (comp: Competition): Promise<PermissionsCheckResponse> => {
  const { emailAddress, guid, host, proofingToken } =
    comp.onlineMagPageComps.onlineMagPage.onlineMagPages.onlineMagIssue;

  const url = new URL(`https://${host}/api/v1/comp/init/${comp.id}`);
  url.searchParams.append('IGUID', guid);
  url.searchParams.append('PT', proofingToken);
  url.searchParams.append('NH', host);
  url.searchParams.append('E', emailAddress);

  return fetch(url.href, {
    method: 'GET',
    credentials: 'include',
    mode: 'cors'
  })
    .then(processJsonResponse)
    .then(unvalidatedJson => {
      if (is(unvalidatedJson, permissionsCheckResponse)) {
        return Promise.resolve(unvalidatedJson);
      }

      return Promise.reject(new Error(SERVER_ERROR_MESSAGE));
    });
};

const fetchQuestions = (comp: Competition): Promise<GetQuestionsResponse> => {
  const { emailAddress, guid, host, proofingToken } =
    comp.onlineMagPageComps.onlineMagPage.onlineMagPages.onlineMagIssue;

  const url = new URL(`https://${host}/api/v1/comp/questions/${comp.id}`);

  url.searchParams.append('IGUID', guid);
  url.searchParams.append('PT', proofingToken);
  url.searchParams.append('NH', host);
  url.searchParams.append('E', emailAddress);

  return fetch(url.href, {
    method: 'GET',
    credentials: 'include',
    mode: 'cors'
  })
    .then(processJsonResponse)
    .then(unvalidatedJson => {
      if (is(unvalidatedJson, getQuestionsResponse)) {
        return Promise.resolve(unvalidatedJson);
      }

      if (is(unvalidatedJson, errorResponse)) {
        return Promise.reject(new Error(unvalidatedJson.Message));
      }

      return Promise.reject(new Error(SERVER_ERROR_MESSAGE));
    });
};

const submitQuestions = (
  comp: Competition,
  usersAnswers: StateType['usersAnswers'],
  feedback: string
): Promise<SubmissionResponse> => {
  const { emailAddress, host, onlineMagIssueVisitID, guid, proofingToken } =
    comp.onlineMagPageComps.onlineMagPage.onlineMagPages.onlineMagIssue;

  const url = new URL(`https://${host}/api/v1/comp/entry/${comp.id}`);
  url.searchParams.append('IGUID', guid);
  url.searchParams.append('PT', proofingToken);
  url.searchParams.append('NH', host);

  return fetch(url.href, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      answers: [...usersAnswers.values()],
      email: emailAddress,
      visitID: onlineMagIssueVisitID,
      feedback: feedback
    })
  })
    .then(processJsonResponse)
    .then(unvalidatedJson => {
      if (is(unvalidatedJson, submissionResponse)) {
        return Promise.resolve(unvalidatedJson);
      }

      return Promise.reject(new Error(SERVER_ERROR_MESSAGE));
    });
};

const submitPlaceholders = (
  comp: Competition,
  resultInfo: SubmissionResponse
): Promise<SubmissionResponse> => {
  const { emailAddress, host, onlineMagIssueVisitID, guid, proofingToken } =
    comp.onlineMagPageComps.onlineMagPage.onlineMagPages.onlineMagIssue;

  const url = new URL(`https://${host}/api/v1/comp/certificate/${comp.id}`);
  url.searchParams.append('IGUID', guid);
  url.searchParams.append('PT', proofingToken);
  url.searchParams.append('NH', host);

  return fetch(url.href, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email: emailAddress,
      readerBadgeID: resultInfo.result.readerBadgeID,
      resultID: resultInfo.result.resultID,
      userPlaceholders: resultInfo.result.badgePlaceholderList,
      visitID: onlineMagIssueVisitID
    })
  })
    .then(processJsonResponse)
    .then(unvalidatedJson => {
      if (is(unvalidatedJson, submissionResponse)) {
        return Promise.resolve(unvalidatedJson);
      }

      return Promise.reject(new Error(SERVER_ERROR_MESSAGE));
    });
};

/*  GET ENTRY ATTEMPT RESPONSE */
export const entryResponse = object({
  onlineMagPageCompID: number(),
  onlineMagPageCompTitle: string(),
  result: object({
    passed: number(),
    score: number(),
    min: number(),
    max: number()
  }),
  questions: nullable(
    array(
      object({
        id: number(),
        prompt: string(),
        type: number(),
        selectedAnswerID: nullable(number()),
        comments: nullable(string()),
        correctAnswerIDs: array(number()),
        correct: number()
      })
    )
  ),
  comments: nullable(string())
});

export type EntryResponse = Infer<typeof entryResponse>;

const fetchEntry = (
  comp: Competition,
  onlineMagPageCompEntryID: number
): Promise<EntryResponse> => {
  const { guid, host, proofingToken } = comp.onlineMagPageComps.onlineMagPage.onlineMagPages.onlineMagIssue;

  const url = new URL(`https://${host}/api/v1/comp/entry/${onlineMagPageCompEntryID}`);
  url.searchParams.append('IGUID', guid);
  url.searchParams.append('PT', proofingToken);
  url.searchParams.append('NH', host);

  return fetch(url.href, {
    method: 'GET',
    credentials: 'include',
    mode: 'cors'
  }).then(processJsonResponse) as Promise<EntryResponse>;
};

export { fetchQuestions, permissionsCheck, submitQuestions, submitPlaceholders, fetchEntry };
